import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import AnchorLink from "../../anchor-link";
import MergedWords from "@default/js/microsites/rbx-company/general-components/merged-words";
import { useSelector } from "react-redux";
import CustomHyphenation from "@default/general-components/custom-hyphenation";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import RichText from "@default/general-components/rich-text";

/**
 * A module representing a text paragraph.
 */
const ParagraphText = ({ content, viewMode }) => {
  const textModule = useRef();

  const { simpleDesign } = useAccessibilityContext();

  // set a shadow element for hover of links, since we need the other elements for border and icon.
  useEffect(() => {
    // get all links inside of the text module
    let links = textModule?.current.querySelectorAll("a");

    // Loop through all links, but only get highlighted Links and Downloads
    [...links].map((link) => {
      if (
        link.getElementsByClassName("highlighted-link")[0] ||
        link.dataset.entityType === "media"
      ) {
        if (link?.querySelector("span")) {
          link?.classList.add("highlighted-link-wrapper");

          // Wrap the text content of .highlighted-link in a span so that
          // text can be truncated independently of the ::after arrow icon.
          const highlightedLink = link.getElementsByClassName("highlighted-link")[0];
          if (highlightedLink) {
            const textSpan = document.createElement("span");
            textSpan.classList.add("highlighted-link__text");
            [...highlightedLink.childNodes].forEach((node) => {
              // Skip the pill-border SVG inserted by injectPillBorders
              if (!(node.nodeType === Node.ELEMENT_NODE && node.hasAttribute("data-pill-border"))) {
                textSpan.appendChild(node);
              }
            });
            highlightedLink.appendChild(textSpan);
          }
        }
      }
    });
  }, []);

  const microSite = useSelector((reduxStore) => reduxStore.appStore.microSite),
    sectionClassNames = classNames({
      "paragraph paragraph-text": true,
      "no-padding-bottom": content.fieldReduceSpacing,
      "orientation-left":
        content.fieldTextOrientation === "left" && !simpleDesign,
      "orientation-right":
        content.fieldTextOrientation === "right" && !simpleDesign,
      "show-border": content.fieldShowBorder,
      "no-headline": !content.fieldHeading,
    }),
    gridSizes = classNames({
      "text-wrapper": true,
      "col-12 col-lg-6 standard":
        viewMode !== "collapse" && !microSite && !simpleDesign,
      "col-12 col-lg-5 standard":
        viewMode !== "collapse" && microSite === "rbx-company" && !simpleDesign,
      "col-12": viewMode === "collapse" || simpleDesign,
      "offset-lg-6":
        viewMode !== "collapse" &&
        content.fieldTextOrientation === "right" &&
        !content.fieldHeading &&
        !simpleDesign,
    }),
    titleWrapperClasses = classNames({
      "title-wrapper col-12": true,
      "col-lg-6": viewMode !== "collapse" && !simpleDesign,
    });

  return (
    <section
      className={sectionClassNames}
      data-darkmode={content.fieldDarkMode}
      ref={textModule}
    >
      {/*<div className={"border"}/>*/}
      <div className="container">
        <div className="row">
          {content.fieldHeading && (
            <div className={titleWrapperClasses}>
              {content.fieldAnchorLink && (
                <AnchorLink link={content.fieldAnchorLink} />
              )}
              {microSite === "rbx-company" && viewMode !== "collapse" ? (
                <h2 className="headline">
                  <MergedWords words={content.fieldHeading} type={"border"} />
                </h2>
              ) : (
                <h2 className="headline">
                  <CustomHyphenation length={18}>
                    {content.fieldHeading}
                  </CustomHyphenation>
                </h2>
              )}
            </div>
          )}
          <div className={gridSizes}>
            <RichText
              className="text body-m"
              html={content.fieldText}
              pillSelectors={[
                ".highlighted-link",
                "a[data-entity-type='media']",
              ]}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

ParagraphText.propTypes = {
  /**
   * The modules content.
   */
  viewMode: PropTypes.string,
  content: PropTypes.shape({
    fieldDarkMode: PropTypes.bool,
    fieldHeading: PropTypes.string,
    fieldReduceSpacing: PropTypes.bool,
    fieldTextOrientation: PropTypes.oneOf(["left", "right"]),
    fieldShowBorder: PropTypes.bool,
    fieldAnchorLink: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
    fieldText: PropTypes.string.isRequired,
  }).isRequired,
};

export default ParagraphText;
